import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactElement } from 'react';
import { cn } from '@/shared/ui';
import { pageWindowOf } from './page-window';

/** 이전·다음 — 툴바 필터 칩과 같은 36px 가는 테두리 원. 비활성은 흐리게만 하고 자리는 지킨다. */
const STEP_BUTTON_CLASS_NAME =
  'grid size-9 cursor-pointer place-items-center rounded-full border border-foreground/20 text-foreground transition-colors hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-foreground/20';

/** 번호 — 글자만 두고, 현재 페이지는 제출 버튼과 같은 흑연 원으로 채운다. */
const PAGE_BUTTON_CLASS_NAME =
  'grid h-9 min-w-9 cursor-pointer place-items-center rounded-full px-2 text-sm tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface StorePaginationProps {
  readonly page: number;
  readonly pageCount: number;
  readonly onPageChange: (page: number) => void;
}

/** 번호형 페이지네이션(‹ 1 2 3 ›) — 페이지 상태는 URL 이 단일 원천이다. 그리드와는 선 없이 여백으로만 띄운다. */
export const StorePagination = ({
  page,
  pageCount,
  onPageChange,
}: StorePaginationProps): ReactElement => {
  // 범위 밖 번호의 URL 보정은 effect(커밋 뒤)라 한 프레임은 page > pageCount 로
  // 들어올 수 있다 — 클램프해서 그 프레임에도 활성 표시·이동 대상이 어긋나지 않게.
  const activePage = Math.min(Math.max(page, 1), pageCount);

  return (
    <nav aria-label="페이지" className="flex items-center justify-center gap-2 pt-10">
      <button
        type="button"
        aria-label="이전 페이지"
        disabled={activePage <= 1}
        onClick={() => onPageChange(activePage - 1)}
        className={STEP_BUTTON_CLASS_NAME}
      >
        <ChevronLeft aria-hidden className="size-4" strokeWidth={1.5} />
      </button>

      <div className="flex items-center gap-1 px-2">
        {pageWindowOf(activePage, pageCount).map((pageNumber) => (
          <button
            key={pageNumber}
            type="button"
            aria-current={pageNumber === activePage ? 'page' : undefined}
            onClick={() => onPageChange(pageNumber)}
            className={cn(
              PAGE_BUTTON_CLASS_NAME,
              pageNumber === activePage
                ? 'bg-foreground font-semibold text-background'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {pageNumber}
          </button>
        ))}
      </div>

      <button
        type="button"
        aria-label="다음 페이지"
        disabled={activePage >= pageCount}
        onClick={() => onPageChange(activePage + 1)}
        className={STEP_BUTTON_CLASS_NAME}
      >
        <ChevronRight aria-hidden className="size-4" strokeWidth={1.5} />
      </button>
    </nav>
  );
};
